import React, { useState, ChangeEvent } from "react";
import "./signUp.less";
import { toast } from "react-toastify";
import service from "../../services/service";
import FlyoutLayout from "../../shared/FlyoutLayout/FlyoutLayout";

interface SignUpProps {
  isOpen: boolean;
  closeModal: () => void;
  onSwitchToLogin: () => void;
}

const SignUp: React.FC<SignUpProps> = ({
  isOpen,
  closeModal,
  onSwitchToLogin,
}) => {
  const displayName = "cmp-signup";
  const mailLogo = new URL("../../assets/mail.svg", import.meta.url).href;
  const lockLogo = new URL("../../assets/lock.svg", import.meta.url).href;
  const userLogo = new URL("../../assets/user.svg", import.meta.url).href;

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const isEmailValid = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isPasswordValid = (value: string) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
      value
    );

  const doPasswordsMatch = password === confirmPassword;

  const isFormValid =
    fullName.trim() !== "" &&
    isEmailValid(email) &&
    isPasswordValid(password) &&
    doPasswordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) {
      toast.error("Please fix form errors before submitting");
      return;
    }

    try {
      setLoading(true);
      await service.register(fullName, email, password);
      toast.success(
        "Registration successful! Please check your email for OTP."
      );
      onSwitchToLogin();
    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data.message || "Registration failed");
      } else {
        toast.error("Unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <FlyoutLayout
      displayName={displayName}
      isOpen={isOpen}
      closeModal={closeModal}
      contentPadding="80px 32px"
      contentAlign="center"
      contentJustify="center"
    >
      <div className={`${displayName}__header`}>
        <h2 className={`${displayName}__title`}>
          <span className={`${displayName}__title-bold`}>
            You’re just one step away
          </span>
        </h2>
      </div>

      <form className={`${displayName}__form`} onSubmit={handleSubmit}>
        {/* Full Name */}
        <div className={`${displayName}__field-group`}>
          <label htmlFor="fullname" className={`${displayName}__label`}>
            Full Name
          </label>
          <div className={`${displayName}__field`}>
            <div className={`${displayName}__input-area`}>
              <img
                className={`${displayName}__icon`}
                src={userLogo}
                alt="user"
              />
              <input
                id="fullname"
                className={`${displayName}__input`}
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                required
              />
            </div>
          </div>
        </div>

        {/* Email */}
        <div className={`${displayName}__field-group`}>
          <label htmlFor="email" className={`${displayName}__label`}>
            Email
          </label>
          <div className={`${displayName}__field`}>
            <div className={`${displayName}__input-area`}>
              <img
                className={`${displayName}__icon`}
                src={mailLogo}
                alt="mail"
              />
              <input
                id="email"
                className={`${displayName}__input`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
          </div>
          {!isEmailValid(email) && email !== "" && (
            <p className={`${displayName}__error-text`}>
              Invalid email address
            </p>
          )}
        </div>

        {/* Password */}
        <div className={`${displayName}__field-group`}>
          <label htmlFor="password" className={`${displayName}__label`}>
            Password
          </label>
          <div className={`${displayName}__field`}>
            <div className={`${displayName}__input-area`}>
              <img
                className={`${displayName}__icon`}
                src={lockLogo}
                alt="lock"
              />
              <input
                id="password"
                className={`${displayName}__input`}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create password"
                required
              />
            </div>
          </div>
          {!isPasswordValid(password) && password !== "" && (
            <p className={`${displayName}__error-text`}>
              Password must be at least 8 characters, include one uppercase, one
              lowercase, one number, and one special character.
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div className={`${displayName}__field-group`}>
          <label htmlFor="confirmPassword" className={`${displayName}__label`}>
            Confirm Password
          </label>
          <div className={`${displayName}__field`}>
            <div className={`${displayName}__input-area`}>
              <img
                className={`${displayName}__icon`}
                src={lockLogo}
                alt="lock"
              />
              <input
                id="confirmPassword"
                className={`${displayName}__input`}
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                required
              />
            </div>
          </div>
          {!doPasswordsMatch && confirmPassword !== "" && (
            <p className={`${displayName}__error-text`}>
              Passwords do not match
            </p>
          )}
        </div>

        {/* Submit */}
        <div className={`${displayName}__submit-btn`}>
          <button
            type="submit"
            className={`${displayName}__submit`}
            disabled={loading || !isFormValid}
          >
            {loading ? "Creating..." : "Create Account"}
          </button>
        </div>
      </form>

      <p className={`${displayName}__login-text`}>
        Already have an account?{" "}
        <button
          type="button"
          className={`${displayName}__link`}
          onClick={onSwitchToLogin}
        >
          Login
        </button>
      </p>
    </FlyoutLayout>
  );
};

export default SignUp;
